import { useTranslation } from "react-i18next"
import { cn } from "@nexus/ui"

import type { PersonaOnboardingReviewItem } from "../../types/personas.types"
import {
  PERSONA_NEW_STATUS_CHIP_CLASS,
  PERSONA_NEW_STATUS_DOT_CLASS,
  PERSONA_STATUS_BADGE_BASE,
} from "../../lib/persona-status"
import type { PersonasOnboardingCardVariant } from "./persona-onboarding-card.types"

/** Figma status chips — fill + label only (no border), 6px radius, 3×9 padding. */
const STATUS_PILL_BASE = PERSONA_STATUS_BADGE_BASE

export function StatusPill({
  status,
  variant,
  hasNote = false,
}: {
  status: PersonaOnboardingReviewItem["decisionStatus"]
  variant: PersonasOnboardingCardVariant
  hasNote?: boolean
}) {
  const { t } = useTranslation()
  if (status === "PENDING") {
    return (
      <span className={cn(STATUS_PILL_BASE, PERSONA_NEW_STATUS_CHIP_CLASS)}>
        <span aria-hidden className={PERSONA_NEW_STATUS_DOT_CLASS} />
        {/* One state, one name: an undecided card is SUGGESTED in the DB and
            reads as New in the library, so the wizard says New too (AD-2518). */}
        <span aria-hidden>
          {t(
            variant === "industry"
              ? "personas.onboarding.industry.statusSuggested"
              : "personas.onboarding.review.statusSuggested",
            "New",
          )}
        </span>
        <span className="sr-only">
          {t("personas.status.newAria", "New — awaiting your review")}
        </span>
      </span>
    )
  }
  if (status === "ACCEPTED") {
    return (
      <span
        className={cn(
          STATUS_PILL_BASE,
          variant === "industry"
            ? "bg-[var(--violet-subtle)] text-[var(--violet-subtle-foreground)]"
            : "bg-success-subtle text-success-subtle-foreground",
        )}
      >
        {t(
          variant === "industry"
            ? "personas.onboarding.industry.statusAccepted"
            : "personas.onboarding.review.statusAccepted",
          variant === "industry" ? "Added" : "Approved",
        )}
      </span>
    )
  }
  if (hasNote && variant !== "industry") {
    return (
      <span className={cn(STATUS_PILL_BASE, "bg-warning-subtle text-warning-subtle-foreground")}>
        {t("personas.onboarding.review.statusRefining", "Refining")}
      </span>
    )
  }
  return (
    <span className={cn(STATUS_PILL_BASE, "bg-error-subtle text-error-subtle-foreground")}>
      {t(
        variant === "industry"
          ? "personas.onboarding.industry.statusRejected"
          : "personas.onboarding.review.statusRejected",
        "Rejected",
      )}
    </span>
  )
}
